import { isRef, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useAsyncData, showError } from "#app";

const flattenStrapiData = (data) => {
  if (!data || typeof data !== "object") return data;

  if (Array.isArray(data)) {
    return data.map(flattenStrapiData);
  }

  // Handle Strapi v4 item structure: { id, attributes: { ... } }
  if (data.id !== undefined && data.attributes !== undefined) {
    return {
      id: data.id,
      ...flattenStrapiData(data.attributes),
    };
  }

  // Handle Strapi v4 relation/data structure: { data: [...] } or { data: { id, attributes } }
  // Only unwrap if 'data' is the primary content and there are no other significant fields (like 'title', 'slug', etc.)
  if (data.data !== undefined) {
    const keys = Object.keys(data);
    // If it only has 'data' and maybe 'meta', it's a wrapper
    if (keys.length === 1 || (keys.length === 2 && keys.includes("meta"))) {
      return flattenStrapiData(data.data);
    }
  }

  // Regular object recursion
  const flattened = {};
  for (const key in data) {
    flattened[key] = flattenStrapiData(data[key]);
  }
  return flattened;
};

export const useApi = (endpoint, isBlogRelated, options = {}) => {
  const { locale } = useI18n();
  const { immediate = true } = options;

  // Generate a unique key for each API call to prevent data collision
  // Using the FULL endpoint to ensure reactivity when query params change
  const asyncKey = computed(() => {
    const resolvedEndpoint = isRef(endpoint) ? endpoint.value : endpoint;
    return `api-${resolvedEndpoint}-${locale.value}`;
  });

  const asyncResult = useAsyncData(
    asyncKey.value,
    () => {
      const resolvedEndpoint = isRef(endpoint) ? endpoint.value : endpoint;
      if (!resolvedEndpoint) {
        return null;
      }
      return $fetch("/api/strapi-get", {
        params: {
          endpoint: resolvedEndpoint,
          locale: locale.value,
        },
      });
    },
    {
      watch: [asyncKey],
      immediate,
    },
  );

  // Manual 404 trigger - allows explicit control in pages
  const triggerError = (statusCode = 404, message = "Sayfa bulunamadı") => {
    showError({
      statusCode,
      statusMessage: message,
      fatal: true,
    });
  };

  // Computed properties match existing structure but are side-effect free
  const resultData = computed(() => {
    if (!asyncResult.data.value) return null;
    // If it's already an array, flatten each item
    if (Array.isArray(asyncResult.data.value.data)) {
      return asyncResult.data.value.data.map(flattenStrapiData);
    }
    // Otherwise flatten the whole thing (handles single objects and wrappers)
    return flattenStrapiData(asyncResult.data.value.data ?? asyncResult.data.value);
  });
  
  const resultMeta = computed(() => asyncResult.data.value?.meta ?? null);

  // Smart fetch: Waits for the initial auto-fetch to prevent duplicate calls, 
  // or fetches manually if it was lazy/empty. Use force=true to force a refresh.
  const smartFetchData = async (force = false) => {
    if (force) {
      return await asyncResult.refresh();
    }
    
    // If an ongoing auto-fetch is happening, just wait for it quietly
    if (asyncResult.pending.value) {
      await asyncResult;
      return resultData.value;
    }

    // If data is already populated (e.g. from SSR hydration), return it
    if (asyncResult.data.value !== null && asyncResult.data.value !== undefined) {
      return resultData.value;
    }

    // Fallback: If it's empty and not pending (maybe immediate:false), execute it
    return await asyncResult.execute();
  };

  return {
    data: resultData,
    meta: resultMeta,
    error: asyncResult.error,
    pending: asyncResult.pending,
    fetchData: smartFetchData,
    triggerError,
  };
};
